/*
 * VerificarCorreoPendiente.jsx — Pantalla post-registro.
 *
 * ago-2026 · ARREGLO DEL INCIDENTE DE PRODUCCIÓN. Antes esta pantalla se quedaba congelada
 * diciendo "Confirma tu correo para entrar" para siempre y su botón de reenvío mostraba un
 * toast verde con solo recibir un 200 — aunque el backend no hubiera enviado nada. Un usuario
 * real verificó su correo en otra pestaña, volvió aquí y siguió presionando "Reenviar" durante
 * minutos creyendo que el sistema estaba roto.
 *
 * Ahora la pantalla lee `codigo` de la respuesta y reacciona de verdad:
 *   ya_verificado → deja de ofrecer reenvíos y manda a iniciar sesión
 *   enviado       → confirma el envío
 *   espera_60s    → dice cuántos segundos faltan, con cuenta regresiva
 *   fallo_envio   → avisa en ROJO que el correo NO salió (antes se veía como éxito)
 *   neutral       → mensaje anti-enumeración de siempre
 *
 * NO se marca a nadie como verificado desde aquí. La única forma sigue siendo el clic en el
 * enlace del correo (`/auth/verificar-correo`).
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MailCheck, RefreshCcw, Loader2, ArrowRight, ShieldCheck, AlertTriangle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { AuthShell } from "@/components/auth/AuthShell";

export default function VerificarCorreoPendiente() {
  const [params] = useSearchParams();
  const emailInicial = params.get("email") || "";
  const entityEstatus = params.get("entity_estatus") || "";
  // El alta ahora informa si el correo salió de verdad (`verificacion_enviada`).
  const envioFallidoEnAlta = params.get("envio") === "fallido";
  const [email, setEmail] = useState(emailInicial);
  const [reenviando, setReenviando] = useState(false);
  const [resultado, setResultado] = useState(null); // {codigo, mensaje}
  const [espera, setEspera] = useState(0);

  useEffect(() => {
    if (espera <= 0) return;
    const t = setTimeout(() => setEspera((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [espera]);

  const reenviar = async () => {
    if (!email) {
      toast.error("Escribe tu correo para reenviar el enlace.");
      return;
    }
    setReenviando(true);
    try {
      const { data } = await api.post("/auth/reenviar-verificacion", { email });
      setResultado({ codigo: data?.codigo || "neutral", mensaje: data?.mensaje || "" });
      if (data?.codigo === "espera_60s") setEspera(data?.reintentar_en || 60);
      if (data?.codigo === "fallo_envio") toast.error(data.mensaje);
      else if (data?.codigo === "ya_verificado") toast.success(data.mensaje);
      else if (data?.enviado) toast.success(data.mensaje);
      else toast.info(data?.mensaje || "");
    } catch (e) {
      setResultado({ codigo: "error", mensaje: apiError(e) });
      toast.error(apiError(e));
    } finally {
      setReenviando(false);
    }
  };

  const yaVerificado = resultado?.codigo === "ya_verificado";

  return (
    <AuthShell>
      <div data-testid="verif-pendiente-shell" className="space-y-5">
        <div className="text-center">
          <div className={`mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full ${yaVerificado ? "bg-emerald-100" : "bg-emerald-50"}`}>
            {yaVerificado ? <ShieldCheck className="h-7 w-7 text-emerald-600" /> : <MailCheck className="h-7 w-7 text-emerald-600" />}
          </div>
          <h1 className="font-heading text-xl font-semibold text-slate-900" data-testid="verif-pendiente-title">
            {yaVerificado ? "Tu correo ya está verificado" : "Confirma tu correo para entrar"}
          </h1>
          <p className="mt-2 text-sm text-slate-600" data-testid="verif-pendiente-lead">
            {yaVerificado ? (
              <>Ya no necesitas hacer nada más con el correo. Inicia sesión para continuar.</>
            ) : (
              <>
                Tu cuenta se creó. Enviamos un enlace de verificación a <b className="font-mono">{emailInicial || "tu correo"}</b>.
                <br />
                Haz clic en ese enlace para poder iniciar sesión — <b>expira en 1 hora</b> y sirve una sola vez.
              </>
            )}
          </p>
        </div>

        {envioFallidoEnAlta && !yaVerificado && (
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800"
               data-testid="verif-pendiente-alta-fallida">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
            <span>
              <b>No pudimos enviar el correo al crear tu cuenta.</b> Tu cuenta sí quedó registrada.
              Pide un enlace nuevo abajo.
            </span>
          </div>
        )}

        {yaVerificado ? (
          <Link to="/login" className="block" data-testid="verif-pendiente-ir-login-cta">
            <Button className="w-full bg-primary hover:bg-sidebar-hover">
              Ir a iniciar sesión <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        ) : (
          <>
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="verif-pendiente-hint">
              <b>¿No llegó?</b> Revisa la carpeta de spam o promociones. Si sigue sin aparecer, pide un nuevo enlace abajo.
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-slate-700">
                Correo con el que te registraste
              </label>
              <Input
                data-testid="verif-pendiente-email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setResultado(null); }}
                placeholder="tu.correo@dominio.mx"
              />

              {resultado?.codigo === "enviado" && (
                <div className="flex items-center gap-1.5 text-xs text-emerald-700" data-testid="verif-pendiente-ok">
                  <ShieldCheck className="h-3.5 w-3.5" /> {resultado.mensaje}
                </div>
              )}
              {resultado?.codigo === "espera_60s" && (
                <div className="flex items-center gap-1.5 text-xs text-amber-700" data-testid="verif-pendiente-espera">
                  <Clock className="h-3.5 w-3.5" />
                  {espera > 0 ? `Ya te enviamos un enlace. Puedes pedir otro en ${espera} s.` : resultado.mensaje}
                </div>
              )}
              {(resultado?.codigo === "fallo_envio" || resultado?.codigo === "error") && (
                <div className="flex items-start gap-1.5 rounded-md border border-red-200 bg-red-50 px-2.5 py-2 text-xs text-red-800"
                     data-testid="verif-pendiente-fallo">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-600" /> {resultado.mensaje}
                </div>
              )}
              {resultado?.codigo === "neutral" && (
                <div className="text-xs text-slate-500" data-testid="verif-pendiente-neutral">
                  {resultado.mensaje}
                </div>
              )}

              <Button
                data-testid="verif-pendiente-reenviar-btn"
                type="button"
                variant="outline"
                disabled={reenviando || !email || espera > 0}
                onClick={reenviar}
                className="w-full">
                {reenviando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCcw className="mr-2 h-4 w-4" />}
                {espera > 0 ? `Reenviar en ${espera} s` : "Reenviar enlace de verificación"}
              </Button>
            </div>
          </>
        )}

        {entityEstatus === "pendiente_aprobacion" && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="verif-pendiente-cortesia">
            Tu cuenta se registró con código de cortesía. Además de verificar tu correo, la documentación pasará a revisión antes de activarse.
          </div>
        )}

        {!yaVerificado && (
          <div className="border-t border-slate-100 pt-4 text-center">
            <p className="text-sm text-slate-500" data-testid="verif-pendiente-yaverifique">
              ¿Ya verificaste tu correo?
            </p>
            <Link to="/login" className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline" data-testid="verif-pendiente-ir-login">
              Ir a iniciar sesión <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        )}
      </div>
    </AuthShell>
  );
}
